import { c as _c } from "react/compiler-runtime";
import { revalidateLogic } from "@tanstack/react-form";
import { useParams, useRouter } from "next/navigation";
import { startTransition, useRef, useState, useTransition } from "react";
import type z from "zod";
import { SAVE_UTKAST_DEBOUNCE_DELAY } from "@/common/app-config";
import { getAGOversiktHref } from "@/common/route-hrefs";
import { VeiviserSteg } from "@/components/NyPlanSide/LagPlanVeiviser";
import { type OppfolgingsplanFormUnderArbeid, oppfolgingsplanFormUtfylltMedEvalueringPaaminnelseSchema, oppfolgingsplanFormUtfylltSchema } from "@/schema/oppfolgingsplanForm/formValidationSchemas";
import { scrollToAppTopForAG } from "@/utils/scrollToAppTop";
import { oppfolgingsplanFormDefaultValues } from "../form-options";
import { useAppForm } from "./form";
import useFerdigstillOppfolgingsplanAction from "./useFerdigstillOppfolgingsplanAction";
import useOppfolgingsplanUtkastLagring from "./useOppfolgingsplanUtkastLagring";
type FormMeta = {
  submitAction: "fortsettTilOppsummering" | "ferdigstill";
};
const defaultMeta: FormMeta = {
  submitAction: "fortsettTilOppsummering"
};
export default function useOppfolgingsplanForm(t0) {
  const $ = _c(21);
  const {
    initialLagretUtkast,
    initialSistLagretTidspunkt,
    erITiltaksgruppe
  } = t0;
  const {
    narmesteLederId
  } = useParams();
  const [veiviserSteg, setVeiviserSteg] = useState(VeiviserSteg.FYLL_UT_PLAN);
  const [isPendingProceedToOppsummering, startProceedToOppsummeringTransition] = useTransition();
  const [isPendingExitAndContinueLater, startExitAndContinueLaterTransition] = useTransition();
  const focusThisOnValidationErrorsRef = useRef(null);
  const {
    push
  } = useRouter();
  let t1;
  if ($[0] !== initialLagretUtkast) {
    t1 = {
      ...oppfolgingsplanFormDefaultValues,
      ...initialLagretUtkast
    };
    $[0] = initialLagretUtkast;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const initialFormValues = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = revalidateLogic();
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = erITiltaksgruppe ? oppfolgingsplanFormUtfylltMedEvalueringPaaminnelseSchema : oppfolgingsplanFormUtfylltSchema;
  let t4;
  if ($[3] !== t3) {
    t4 = {
      onDynamic: t3
    };
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      focusThisOnValidationErrorsRef.current?.focus();
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const form = useAppForm({
    defaultValues: initialFormValues,
    validationLogic: t2,
    validators: t4,
    listeners: {
      onChange: t5 => {
        const {
          formApi
        } = t5;
        return startLagreUtkastIfChanges({
          values: formApi.state.values,
          skipIfInvalid: true
        });
      },
      onChangeDebounceMs: SAVE_UTKAST_DEBOUNCE_DELAY
    },
    onSubmitInvalid: t6,
    onSubmitMeta: defaultMeta,
    onSubmit: t7 => {
      const {
        value,
        meta
      } = t7;
      if (meta.submitAction === "fortsettTilOppsummering") {
        saveIfChangesAndProceedToOppsummering(value);
      } else {
        if (meta.submitAction === "ferdigstill") {
          startFerdigstillPlanAction({
            formValues: value as z.infer<typeof oppfolgingsplanFormUtfylltSchema>,
            evalueringsDatoIsoString: value.evalueringsDato,
            includeIkkeMedvirketBegrunnelseFieldInFormSnapshot: value.harDenAnsatteMedvirket === "nei",
            evalueringPaaminnelse: value.evalueringPaaminnelse === "true"
          });
        }
      }
    }
  });
  let t8;
  if ($[6] !== initialFormValues || $[7] !== initialSistLagretTidspunkt) {
    t8 = {
      initialFormValues,
      initialSistLagretTidspunkt
    };
    $[6] = initialFormValues;
    $[7] = initialSistLagretTidspunkt;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const {
    isSavingUtkast,
    lagreUtkastError,
    sistLagretTidspunkt,
    startLagreUtkastIfChanges: t9
  } = useOppfolgingsplanUtkastLagring(t8);
  const startLagreUtkastIfChanges = t9;
  const {
    startFerdigstillPlanAction: t10,
    isPendingFerdigstillPlan,
    error: ferdigstillPlanError
  } = useFerdigstillOppfolgingsplanAction();
  const startFerdigstillPlanAction = t10;
  function saveIfChangesAndProceedToOppsummering(values) {
    startProceedToOppsummeringTransition(() => {
      startLagreUtkastIfChanges({
        values,
        onSuccess: () => {
          proceedToOppsummeringSteg();
        }
      });
    });
  }
  const saveIfChangesAndExit = function saveIfChangesAndExit() {
    startExitAndContinueLaterTransition(() => {
      startLagreUtkastIfChanges({
        values: form.state.values,
        onSuccess: () => {
          push(getAGOversiktHref(narmesteLederId));
        }
      });
    });
  };
  function proceedToOppsummeringSteg() {
    startTransition(() => {
      setVeiviserSteg(VeiviserSteg.OPPSUMMERING);
    });
    scrollToAppTopForAG();
  }
  let t11;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = function goBackToFyllUtPlanSteg() {
      startTransition(() => {
        setVeiviserSteg(VeiviserSteg.FYLL_UT_PLAN);
      });
      scrollToAppTopForAG();
    };
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  const goBackToFyllUtPlanSteg = t11;
  let t12;
  if ($[10] !== ferdigstillPlanError || $[11] !== form || $[12] !== isPendingExitAndContinueLater || $[13] !== isPendingFerdigstillPlan || $[14] !== isPendingProceedToOppsummering || $[15] !== isSavingUtkast || $[16] !== lagreUtkastError || $[17] !== saveIfChangesAndExit || $[18] !== sistLagretTidspunkt || $[19] !== veiviserSteg) {
    t12 = {
      form,
      veiviserSteg,
      focusThisOnValidationErrorsRef,
      isSavingUtkast,
      utkastSistLagretTidspunkt: sistLagretTidspunkt,
      isPendingProceedToOppsummering,
      isPendingExitAndContinueLater,
      isPendingFerdigstillPlan,
      saveIfChangesAndExit,
      goBackToFyllUtPlanSteg,
      lagreUtkastError,
      ferdigstillPlanError
    };
    $[10] = ferdigstillPlanError;
    $[11] = form;
    $[12] = isPendingExitAndContinueLater;
    $[13] = isPendingFerdigstillPlan;
    $[14] = isPendingProceedToOppsummering;
    $[15] = isSavingUtkast;
    $[16] = lagreUtkastError;
    $[17] = saveIfChangesAndExit;
    $[18] = sistLagretTidspunkt;
    $[19] = veiviserSteg;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  return t12;
}